import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { article, jsonLdScript } from '@/lib/schema'

export const Route = createFileRoute('/_site/guides/monitoring-as-code')({
  component: MonitoringAsCodePage,
  head: () => ({
    meta: [
      { title: 'Monitoring as code — the Uptrack CLI — Uptrack' },
      {
        name: 'description',
        content:
          'Define monitors in a version-controlled YAML file and sync them from CI with the uptrack CLI. Install, authenticate and apply.',
      },
      { property: 'og:title', content: 'Monitoring as code — the Uptrack CLI' },
      {
        property: 'og:description',
        content: 'Keep your monitors in Git and apply them from CI with the uptrack CLI — review in PRs, sync on merge.',
      },
      {
        property: 'og:image',
        content: 'https://uptrack.app/og/guides-monitoring-as-code.png',
      },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/guides/monitoring-as-code' }],
    scripts: [
      jsonLdScript(
        article({
          headline: 'Monitoring as code with the Uptrack CLI',
          description: 'Define monitors in a version-controlled YAML file and sync them from CI with the uptrack CLI. Install, authenticate and apply.',
          url: 'https://uptrack.app/guides/monitoring-as-code',
          datePublished: '2026-06-14',
          imageUrl: 'https://uptrack.app/og/guides-monitoring-as-code.png',
        }),
      ),
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: 'Monitoring as code with the Uptrack CLI',
          description: 'Install the uptrack CLI, define monitors in YAML, and apply them from your shell or CI.',
          step: [
            { '@type': 'HowToStep', name: 'Install the CLI' },
            { '@type': 'HowToStep', name: 'Authenticate with an API key' },
            { '@type': 'HowToStep', name: 'Define monitors in YAML' },
            { '@type': 'HowToStep', name: 'Apply from your shell or CI' },
          ],
        }),
      },
    ],
  }),
})

function Code({ children }: { children: string }) {
  return (
    <pre tabIndex={0} className="mb-4 overflow-x-auto rounded-lg bg-foreground/[0.06] p-4 font-mono text-sm">
      <code>{children}</code>
    </pre>
  )
}

function MonitoringAsCodePage() {
  return (
    <>
      <>
        <article
          className="article-prose container mx-auto max-w-2xl px-4 py-16"
        >
          <p className="font-sans mb-3 text-sm font-medium uppercase tracking-wide text-primary">Guide</p>
          <h1 className="font-sans mb-4 text-4xl font-semibold">Monitoring as code</h1>
          <p className="mb-8 text-lg leading-[1.7] text-muted-foreground">
            Click-ops is fine until you have thirty monitors and no record of who changed what. The{' '}
            <code className="font-mono text-base">uptrack</code> CLI lets you keep your monitors in a YAML file
            in your repo, review changes in a pull request, and sync them from CI on merge — the same monitors,
            now under version control.
          </p>

          <section className="mb-10">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Step 1 — Install the CLI</h2>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              It&rsquo;s a single static binary (no runtime to install). On macOS or Linux:
            </p>
            <Code>{`curl -fsSL https://raw.githubusercontent.com/Uptrack-App/uptrack-cli/main/install.sh | sh`}</Code>
            <p className="text-lg leading-[1.7] text-body">
              Or download a binary for your platform from the{' '}
              <a
                href="https://github.com/Uptrack-App/uptrack-cli/releases/latest"
                className="font-medium text-primary hover:underline"
              >
                latest release
              </a>{' '}
              and put it on your <code className="font-mono text-base">PATH</code>.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Step 2 — Authenticate</h2>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              Create an API key under <strong className="text-foreground">Settings → API keys</strong> and put it
              in your environment:
            </p>
            <Code>{`export UPTRACK_API_KEY=utk_…`}</Code>
            <p className="text-lg leading-[1.7] text-body">
              Now <code className="font-mono text-base">uptrack list</code> shows your monitors with their status
              and 30-day uptime; <code className="font-mono text-base">uptrack status</code> prints an up/down
              summary.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Step 3 — Define monitors in YAML</h2>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              Already have monitors? <code className="font-mono text-base">uptrack export -o uptrack.yaml</code>{' '}
              dumps them to a file to commit. Starting fresh, the format is small:
            </p>
            <Code>{`monitors:
  - name: Homepage
    url: https://example.com
    type: http
    interval: 60
  - name: Login keyword
    url: https://example.com/login
    type: keyword
    keyword: "Sign in"`}</Code>
            <p className="text-lg leading-[1.7] text-body">
              Monitors are matched by name. <code className="font-mono text-base">type</code> defaults to{' '}
              <code className="font-mono text-base">http</code>; <code className="font-mono text-base">interval</code>{' '}
              (seconds) and <code className="font-mono text-base">keyword</code> are optional.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Step 4 — Apply from your shell or CI</h2>
            <p className="mb-4 text-lg leading-[1.7] text-body">
              <code className="font-mono text-base">apply</code> creates anything new, updates what changed, and
              leaves the rest alone. Preview first with <code className="font-mono text-base">--dry-run</code>;
              add <code className="font-mono text-base">--prune --yes</code> to also delete monitors not in the file.
            </p>
            <Code>{`uptrack apply -f uptrack.yaml --dry-run   # preview
uptrack apply -f uptrack.yaml             # sync`}</Code>
            <p className="mb-4 text-lg leading-[1.7] text-body">In GitHub Actions, sync on merge:</p>
            <Code>{`- run: curl -fsSL https://raw.githubusercontent.com/Uptrack-App/uptrack-cli/main/install.sh | sh
- run: uptrack apply -f uptrack.yaml
  env:
    UPTRACK_API_KEY: \${{ secrets.UPTRACK_API_KEY }}`}</Code>
          </section>

          <div className="font-sans rounded-lg border border-border bg-muted p-6 sm:p-8">
            <h2 className="text-xl font-semibold">Put your monitors in Git</h2>
            <p className="mt-1 text-sm text-body">
              Pro includes 50 monitors at 30-second checks. Create a key, export, commit, and apply from CI.
            </p>
            <Link to="/signup" className="mt-4 inline-flex items-center font-medium text-primary hover:underline">
              Get started <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
            <span className="mx-3 text-muted-foreground">·</span>
            <a
              href="https://github.com/Uptrack-App/uptrack-cli"
              className="font-medium text-primary hover:underline"
            >
              CLI on GitHub
            </a>
          </div>
        </article>
      </>
      </>
  )
}
